

import { Direction, Directionality } from '@angular/cdk/bidi';
import {
  ComponentRef,
  Directive,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Renderer2,
  Self,
  SimpleChanges,
  ViewContainerRef
} from '@angular/core';
import { NgControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';

import { MwcFormItemFeedbackIconComponent, MwcFormNoStatusService, MwcFormStatusService } from 'mwc/core/form';
import { BooleanInput, NgClassInterface, MwcSizeLDSType, MwcStatus, MwcValidateStatus } from 'mwc/core/types';
import { getStatusClassNames, InputBoolean } from 'mwc/core/util';

@Directive({
  selector: 'input[mwc-input],textarea[mwc-input]',
  exportAs: 'mwcInput',
  host: {
    class: 'mwc-input',
    '[class.mwc-input-disabled]': 'disabled',
    '[class.mwc-input-borderless]': 'mwcBorderless',
    '[class.mwc-input-lg]': `mwcSize === 'large'`,
    '[class.mwc-input-sm]': `mwcSize === 'small'`,
    '[attr.disabled]': 'disabled || null',
    '[class.mwc-input-rtl]': `dir=== 'rtl'`
  }
})
export class MwcInputDirective implements OnChanges, OnInit, OnDestroy {
  static ngAcceptInputType_disabled: BooleanInput;
  static ngAcceptInputType_mwcBorderless: BooleanInput;
  @Input() @InputBoolean() mwcBorderless = false;
  @Input() mwcSize: MwcSizeLDSType = 'small';
  @Input() mwcStatus: MwcStatus = '';
  @Input()
  get disabled(): boolean {
    if (this.ngControl && this.ngControl.disabled !== null) {
      return this.ngControl.disabled;
    }
    return this._disabled;
  }
  set disabled(value: boolean) {
    this._disabled = value != null && `${value}` !== 'false';
  }
  _disabled = false;
  disabled$ = new Subject<boolean>();
  dir: Direction = 'ltr';
  // status
  prefixCls: string = 'mwc-input';
  status: MwcValidateStatus = '';
  statusCls: NgClassInterface = {};
  hasFeedback: boolean = false;
  feedbackRef: ComponentRef<MwcFormItemFeedbackIconComponent> | null = null;
  components: Array<ComponentRef<MwcFormItemFeedbackIconComponent>> = [];
  private destroy$ = new Subject<void>();

  constructor(
    @Optional() @Self() public ngControl: NgControl,
    private renderer: Renderer2,
    private elementRef: ElementRef,
    protected hostView: ViewContainerRef,
    @Optional() private directionality: Directionality,
    @Optional() private mwcFormStatusService?: MwcFormStatusService,
    @Optional() public mwcFormNoStatusService?: MwcFormNoStatusService
  ) {}

  ngOnInit(): void {
    this.mwcFormStatusService?.formStatusChanges
      .pipe(
        distinctUntilChanged((pre, cur) => {
          return pre.status === cur.status && pre.hasFeedback === cur.hasFeedback;
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(({ status, hasFeedback }) => {
        this.setStatusStyles(status, hasFeedback);
      });

    if (this.ngControl) {
      this.ngControl.statusChanges
        ?.pipe(
          filter(() => this.ngControl.disabled !== null),
          takeUntil(this.destroy$)
        )
        .subscribe(() => {
          this.disabled$.next(this.ngControl.disabled!);
        });
    }

    this.dir = this.directionality.value;
    this.directionality.change?.pipe(takeUntil(this.destroy$)).subscribe((direction: Direction) => {
      this.dir = direction;
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    const { disabled, mwcStatus } = changes;
    if (disabled) {
      this.disabled$.next(this.disabled);
    }
    if (mwcStatus) {
      this.setStatusStyles(this.mwcStatus, this.hasFeedback);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setStatusStyles(status: MwcValidateStatus, hasFeedback: boolean): void {
    // set inner status
    this.status = status;
    this.hasFeedback = hasFeedback;
    this.renderFeedbackIcon();
    // render status if mwcStatus is set
    this.statusCls = getStatusClassNames(this.prefixCls, status, hasFeedback);
    Object.keys(this.statusCls).forEach(status => {
      if (this.statusCls[status]) {
        this.renderer.addClass(this.elementRef.nativeElement, status);
      } else {
        this.renderer.removeClass(this.elementRef.nativeElement, status);
      }
    });
  }

  private renderFeedbackIcon(): void {
    if (!this.status || !this.hasFeedback || !!this.mwcFormNoStatusService) {
      // remove feedback
      this.hostView.clear();
      this.feedbackRef = null;
      return;
    }

    this.feedbackRef = this.feedbackRef || this.hostView.createComponent(MwcFormItemFeedbackIconComponent);
    this.feedbackRef.location.nativeElement.classList.add('mwc-input-suffix');
    this.feedbackRef.instance.status = this.status;
    this.feedbackRef.instance.updateIcon();
  }
}
